import React, { useEffect, useMemo, useState } from 'react';
import { Percent, Trash2, X } from 'lucide-react';

import Button from '@components/common/Button';
import { SearchableSelect } from '@components/common/SearchableSelect';
import {
  useDeleteVendorInvoiceRule,
  useUpsertVendorInvoiceRule,
  useVendorInvoiceRules,
} from '@api/hooks';
import type {
  VendorInvoiceRule,
  VendorInvoiceRuleBasis,
  VendorInvoiceRuleSeverity,
  VendorSearchResponse,
} from '@schemas/api';

interface VendorInvoiceRulesModalProps {
  vendor: VendorSearchResponse | null;
  onClose: () => void;
}

const basisOptions: Array<{ value: VendorInvoiceRuleBasis; label: string }> = [
  { value: 'subtotal', label: 'Subtotal' },
  { value: 'pre_tax_total', label: 'Pre-tax total' },
  { value: 'line_total', label: 'Line total' },
];

const severityOptions: Array<{ value: VendorInvoiceRuleSeverity; label: string }> = [
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
  { value: 'info', label: 'Info' },
];

function firstExpectedDiscountRule(rules?: VendorInvoiceRule[]): VendorInvoiceRule | undefined {
  return rules?.find((rule) => rule.rule_type === 'expected_discount');
}

export const VendorInvoiceRulesModal: React.FC<VendorInvoiceRulesModalProps> = ({
  vendor,
  onClose,
}) => {
  const vendorId = vendor?.id || '';
  const { data, isLoading } = useVendorInvoiceRules(vendorId);
  const upsertRule = useUpsertVendorInvoiceRule(vendorId);
  const deleteRule = useDeleteVendorInvoiceRule(vendorId);
  const currentRule = useMemo(
    () => firstExpectedDiscountRule(data?.items) || firstExpectedDiscountRule(vendor?.invoice_rules),
    [data?.items, vendor?.invoice_rules]
  );

  const [discountPercent, setDiscountPercent] = useState('20');
  const [basis, setBasis] = useState<VendorInvoiceRuleBasis>('subtotal');
  const [toleranceAmount, setToleranceAmount] = useState('0.02');
  const [severity, setSeverity] = useState<VendorInvoiceRuleSeverity>('warning');
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!currentRule) {
      setDiscountPercent('20');
      setBasis('subtotal');
      setToleranceAmount('0.02');
      setSeverity('warning');
      setActive(true);
      return;
    }
    setDiscountPercent(String(currentRule.discount_percent));
    setBasis(currentRule.basis);
    setToleranceAmount(String(currentRule.tolerance_amount));
    setSeverity(currentRule.severity);
    setActive(currentRule.active);
  }, [currentRule]);

  if (!vendor) return null;

  const isSaving = upsertRule.isPending;
  const isDeleting = deleteRule.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await upsertRule.mutateAsync({
      rule_type: 'expected_discount',
      discount_percent: Number(discountPercent),
      basis,
      tolerance_amount: Number(toleranceAmount),
      severity,
      active,
      source: 'manual',
    });
    onClose();
  };

  const handleDelete = async () => {
    if (!currentRule) return;
    await deleteRule.mutateAsync(currentRule.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl dark:bg-surface-base-dark">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-content-primary dark:text-content-primary-dark">
              <Percent className="h-5 w-5 text-blue-600" />
              Invoice discount rule
            </h2>
            <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
              {vendor.legal_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-content-muted hover:bg-raised hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary dark:hover:bg-raised-dark dark:hover:text-content-primary-dark"
            aria-label="Close invoice discount rule"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">
              Discount percent
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                required
                value={discountPercent}
                onChange={(event) => setDiscountPercent(event.target.value)}
                className="input mt-1 w-full"
              />
            </label>

            <label className="block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">
              Tolerance
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={toleranceAmount}
                onChange={(event) => setToleranceAmount(event.target.value)}
                className="input mt-1 w-full"
              />
            </label>

            <label className="block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">
              Basis
              <SearchableSelect
                value={basis}
                onChange={(value) => setBasis(value as VendorInvoiceRuleBasis)}
                options={basisOptions.map((option) => ({ id: option.value, label: option.label }))}
                placeholder="Basis"
                clearable={false}
                className="mt-1"
              />
            </label>

            <label className="block text-sm font-medium text-content-secondary dark:text-content-secondary-dark">
              Flag severity
              <SearchableSelect
                value={severity}
                onChange={(value) => setSeverity(value as VendorInvoiceRuleSeverity)}
                options={severityOptions.map((option) => ({ id: option.value, label: option.label }))}
                placeholder="Flag severity"
                clearable={false}
                className="mt-1"
              />
            </label>
          </div>

          <label className="inline-flex items-center gap-2 text-sm font-medium text-content-secondary dark:text-content-secondary-dark">
            <input
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
              className="h-4 w-4 rounded border-default text-primary focus:ring-primary"
            />
            Active
          </label>

          {isLoading && (
            <p className="text-sm text-content-muted">Loading rule...</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div>
              {currentRule && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  loading={isDeleting}
                  disabled={isSaving}
                >
                  <span className="inline-flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </span>
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving || isDeleting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving} disabled={isDeleting}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
